import React, { useCallback, useEffect } from 'react';
import CheckoutLayout from '../../CheckoutLayout';
import { Input, Text } from '~/components';
import { Keyboard } from 'react-native';
import { useCheckout, useSalesFunnel } from '~/stores';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isDateValid } from '~/utils/yup';
import type { RootStackParamList } from '~/navigation/types';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { CheckoutStepNames } from '~/utils/salesFunnel';

interface LicenseSchema {
  registerNumber: number;
  dateOfIssue: string;
}

const schema = yup
  .object()
  .shape({
    registerNumber: yup
      .string()
      .length(11, 'Este campo deve possuir 11 caracteres')
      .required('Insira as informações para continuar'),
    dateOfIssue: isDateValid(),
  })
  .required();

const License: React.FC = () => {
  const { setPage, setInsuranceFormData } = useCheckout();
  const { push } = useAppNavigation();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LicenseSchema>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: LicenseSchema) => {
      Keyboard.dismiss();

      setInsuranceFormData(data);

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);

      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [params.step, push, setInsuranceFormData, setPage]
  );

  useEffect(() => {
    newStepLog(CheckoutStepNames.LICENSE);
  }, [newStepLog]);

  return (
    <CheckoutLayout isValid onSubmit={handleSubmit(onSubmit)}>
      <Text className="text-xl text-gray-600 mb-4">CNH</Text>

      <Text className="text-sm text-gray-400 mb-8">
        Nos informe os número e data da emissão da sua CNH (Carteira Nacional de
        Habilitação)
      </Text>

      <Input
        control={control}
        name="registerNumber"
        label="Nº de Registro"
        placeholder="Insira os números do Registro da CNH"
        error={errors.registerNumber?.message}
        keyboardType="number-pad"
        maxLength={11}
        maskConfig={{ type: 'only-numbers' }}
      />

      <Input
        control={control}
        name="dateOfIssue"
        label="Data de Emissão"
        placeholder="Insira a data de emissão da CNH"
        error={errors.dateOfIssue?.message}
        keyboardType="number-pad"
        maskConfig={{ type: 'custom', mask: '99/99/9999' }}
        maxLength={10}
      />
    </CheckoutLayout>
  );
};

export default License;
